import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon, PlusIcon, TrashIcon, UserIcon, BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import { createProgressSummary, updateProgressSummary } from "../../store/thunks/progressSummaryThunk";
import { resetLoadingStates } from "../../store/slices/progressSummarySlice";
import { fetchClientTreatmentPlans } from "../../store/thunks/treatmentPlanThunk";
import { clearClientTreatmentPlans } from "../../store/slices/treatmentPlanSlice";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import SearchDropdown from "./SearchDropdown";

const ProgressSummaryForm = ({ onClose, onSave, editingProgressSummary = null }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { createLoading, updateLoading } = useSelector((state) => state.progressSummary);
  const { clientTreatmentPlans } = useSelector((state) => state.treatmentPlan);
  const { clinics } = useSelector((state) => state.clinics);

  const isEditing = !!editingProgressSummary;

  // Reset loading states and fetch clinics when form opens
  useEffect(() => {
    console.log(`[Progress Summary Form] Component mounted, resetting loading states`);
    dispatch(resetLoadingStates());
    dispatch(fetchClinics());
  }, [dispatch]);

  const [formData, setFormData] = useState({
    clinicId: user?.clinicId || "",
    clinicName: "",
    clinicAddress: "",
    clinicPhone: "",
    clinicEmail: "",
    clientId: "",
    clientName: "",
    date: new Date().toISOString().split('T')[0],
    dateOfBirth: "",
    chronologicalAge: "",
    supervisingSLP: "",
    supervisingSLPId: "",
    supervisingSLPQualifications: "",
    grade: "",
    school: "",
    clinicians: "",
    clinicianId: "",
    currentCommunicationStatus: "",
    treatmentPlan: "Weekly sessions one-on-one, 60 minutes/week",
    sessionNumber: "",
    goals: [
      {
        id: 1,
        description: "",
        notYetTargeted: false,
        emergingMaximum: false,
        emergingModerate: false,
        emergingMinimal: false,
        achieved: false,
        actions: ""
      }
    ],
    comments: "",
    nextSteps: ""
  });

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [initialSearchValue, setInitialSearchValue] = useState("");
  const [selectedSupervisingSLP, setSelectedSupervisingSLP] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [supervisingSLPSearchValue, setSupervisingSLPSearchValue] = useState("");
  const [clinicianSearchValue, setClinicianSearchValue] = useState("");
  const [selectedTreatmentPlan, setSelectedTreatmentPlan] = useState(null);

  // Calculate chronological age from date of birth
  const calculateChronologicalAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";

    const dob = new Date(dateOfBirth);
    const today = new Date();

    let years = today.getFullYear() - dob.getFullYear();
    let months = today.getMonth() - dob.getMonth();

    // Adjust if the birthday hasn't occurred yet this year
    if (months < 0 || (months === 0 && today.getDate() < dob.getDate())) {
      years--;
      months += 12;
    }

    // Adjust months if we haven't reached the birth day yet this month
    if (today.getDate() < dob.getDate()) {
      months--;
    }

    // Format the age
    if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
    }
  };

  // Auto-calculate chronological age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const calculatedAge = calculateChronologicalAge(formData.dateOfBirth);
      setFormData(prev => ({
        ...prev,
        chronologicalAge: calculatedAge
      }));
    }
  }, [formData.dateOfBirth]);

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && editingProgressSummary) {
      console.log(`[Progress Summary Form] Initializing form for editing:`, editingProgressSummary);

      // Convert date to YYYY-MM-DD format for input
      const formattedDate = editingProgressSummary.date
        ? new Date(editingProgressSummary.date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      const formattedDateOfBirth = editingProgressSummary.dateOfBirth
        ? new Date(editingProgressSummary.dateOfBirth).toISOString().split('T')[0]
        : "";

      setFormData({
        clientId: editingProgressSummary.clientId?._id || "",
        clientName: editingProgressSummary.clientName || "",
        date: formattedDate,
        dateOfBirth: formattedDateOfBirth,
        chronologicalAge: editingProgressSummary.chronologicalAge || "",
        supervisingSLP: editingProgressSummary.supervisingSLP || "",
        supervisingSLPId: editingProgressSummary.supervisingSLPId?._id || editingProgressSummary.supervisingSLPId || "",
        supervisingSLPQualifications: editingProgressSummary.supervisingSLPQualifications || "",
        grade: editingProgressSummary.grade || "",
        school: editingProgressSummary.school || "",
        clinicians: editingProgressSummary.clinicians || "",
        clinicianId: editingProgressSummary.clinicianId?._id || editingProgressSummary.clinicianId || "",
        currentCommunicationStatus: editingProgressSummary.currentCommunicationStatus || "",
        treatmentPlan: editingProgressSummary.treatmentPlan || "Weekly sessions one-on-one, 60 minutes/week",
        sessionNumber: editingProgressSummary.sessionNumber || "",
        goals: editingProgressSummary.goals?.length > 0 ? editingProgressSummary.goals : [
          {
            id: 1,
            description: "",
            notYetTargeted: false,
            emergingMaximum: false,
            emergingModerate: false,
            emergingMinimal: false,
            achieved: false
          }
        ],
        comments: editingProgressSummary.comments || "",
        nextSteps: editingProgressSummary.nextSteps || ""
      });

      // Set initial search value if client is selected
      if (editingProgressSummary.clientName) {
        setInitialSearchValue(editingProgressSummary.clientName);
        if (editingProgressSummary.clientId) {
          setSelectedClient({
            _id: editingProgressSummary.clientId._id || editingProgressSummary.clientId,
            firstName: editingProgressSummary.clientName.split(' ')[0] || '',
            lastName: editingProgressSummary.clientName.split(' ').slice(1).join(' ') || '',
          });
        }
      }

      // Set supervising SLP search value
      if (editingProgressSummary.supervisingSLP) {
        setSupervisingSLPSearchValue(editingProgressSummary.supervisingSLP);
      }

      // Set clinician search value
      if (editingProgressSummary.clinicians) {
        setClinicianSearchValue(editingProgressSummary.clinicians);
      }
    }
  }, [isEditing, editingProgressSummary]);

  // Fetch all treatment plans when client is selected (only for new progress summaries)
  useEffect(() => {
    if (selectedClient && selectedClient._id && !isEditing) {
      console.log(`[Progress Summary Form] Fetching treatment plans for client ${selectedClient._id}`);
      dispatch(fetchClientTreatmentPlans({ clientId: selectedClient._id, page: 1, limit: 100 }));
    }
  }, [selectedClient, isEditing, dispatch]);

  // Cleanup: clear client treatment plans when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearClientTreatmentPlans());
    };
  }, [dispatch]);

  const handleClinicSelect = (e) => {
    const clinicId = e.target.value;
    console.log(`[Progress Summary Form] Selected clinic ID:`, clinicId);

    const clinic = clinics.find(c => c._id === clinicId);
    if (clinic) {
      setSelectedClinic(clinic);
      const fullAddress = `${clinic.address.street}, ${clinic.address.city}, ${clinic.address.province} ${clinic.address.postalCode}`;
      setFormData(prev => ({
        ...prev,
        clinicId: clinic._id,
        clinicName: clinic.name,
        clinicAddress: fullAddress,
        clinicPhone: clinic.phoneNumber,
        clinicEmail: clinic.email
      }));
    }
  };

  const handleClientSelect = (client) => {
    console.log(`[Progress Summary Form] Selected client:`, client);
    setSelectedClient(client);
    setSelectedTreatmentPlan(null); // Reset selected treatment plan when client changes
    setFormData(prev => ({
      ...prev,
      clientId: client._id,
      clientName: `${client.firstName} ${client.lastName}`,
      dateOfBirth: client.dateOfBirth ? new Date(client.dateOfBirth).toISOString().split('T')[0] : ""
    }));
  };

  const handleTreatmentPlanSelect = (e) => {
    const treatmentPlanId = e.target.value;

    if (!treatmentPlanId) {
      setSelectedTreatmentPlan(null);
      return;
    }

    // Find the selected treatment plan from the list
    const treatmentPlans = clientTreatmentPlans?.treatmentPlans?.docs || [];
    const plan = treatmentPlans.find(tp => tp._id === treatmentPlanId);

    if (!plan) return;

    console.log(`[Progress Summary Form] Selected treatment plan:`, plan);
    setSelectedTreatmentPlan(plan);

    // Automatically load goals when treatment plan is selected
    if (plan.goals && plan.goals.length > 0) {
      console.log(`[Progress Summary Form] Auto-loading ${plan.goals.length} goals from treatment plan`);

      const convertedGoals = plan.goals.map((goal, index) => ({
        id: goal.id || index + 1,
        description: goal.description || "",
        notYetTargeted: false,
        emergingMaximum: false,
        emergingModerate: false,
        emergingMinimal: false,
        achieved: false,
        actions: ""
      }));

      setFormData(prev => ({
        ...prev,
        goals: convertedGoals
      }));

      toast.success(`Loaded ${convertedGoals.length} goals from treatment plan`);
    }
  };

  const handleSupervisingSLPSelect = (clinician) => {
    console.log(`[Progress Summary Form] Selected supervising SLP:`, clinician);
    setSelectedSupervisingSLP(clinician);
    const fullName = `${clinician.firstName} ${clinician.lastName}`;
    setFormData(prev => ({
      ...prev,
      supervisingSLP: fullName,
      supervisingSLPId: clinician._id,
      supervisingSLPQualifications: clinician.qualifications || ""
    }));
  };

  const handleClinicianSelect = (clinician) => {
    console.log(`[Progress Summary Form] Selected clinician:`, clinician);
    setSelectedClinician(clinician);
    const fullName = `${clinician.firstName} ${clinician.lastName}`;
    setFormData(prev => ({
      ...prev,
      clinicians: fullName,
      clinicianId: clinician._id
    }));
  };

  const handleInputChange = (field, value) => {
    console.log(`[Progress Summary Form] Updating ${field} with value:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGoalChange = (goalId, field, value) => {
    console.log(`[Progress Summary Form] Updating goal ${goalId} ${field} with value:`, value);
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map(goal => {
        if (goal.id === goalId) {
          // If this is a radio button selection, clear other progress options
          if (['notYetTargeted', 'emergingMaximum', 'emergingModerate', 'emergingMinimal', 'achieved'].includes(field)) {
            return {
              ...goal,
              notYetTargeted: false,
              emergingMaximum: false,
              emergingModerate: false,
              emergingMinimal: false,
              achieved: false,
              [field]: value
            };
          }
          return {
            ...goal,
            [field]: value
          };
        }
        return goal;
      })
    }));
  };

  const handleGoalProgressChange = (goalId, progressValue) => {
    console.log(`[Progress Summary Form] Setting progress for goal ${goalId} to:`, progressValue);
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map(goal => {
        if (goal.id === goalId) {
          return {
            ...goal,
            notYetTargeted: progressValue === 'notYetTargeted',
            emergingMaximum: progressValue === 'emergingMaximum',
            emergingModerate: progressValue === 'emergingModerate',
            emergingMinimal: progressValue === 'emergingMinimal',
            achieved: progressValue === 'achieved'
          };
        }
        return goal;
      })
    }));
  };

  const getGoalProgressValue = (goal) => {
    if (goal.achieved) return 'achieved';
    if (goal.emergingMinimal) return 'emergingMinimal';
    if (goal.emergingModerate) return 'emergingModerate';
    if (goal.emergingMaximum) return 'emergingMaximum';
    if (goal.notYetTargeted) return 'notYetTargeted';
    return '';
  };

  const addGoal = () => {
    console.log(`[Progress Summary Form] Adding new goal`);
    const newGoal = {
      id: Date.now(),
      description: "",
      notYetTargeted: false,
      emergingMaximum: false,
      emergingModerate: false,
      emergingMinimal: false,
      achieved: false,
      actions: ""
    };

    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, newGoal]
    }));
  };

  const removeGoal = (goalId) => {
    console.log(`[Progress Summary Form] Removing goal ${goalId}`);
    if (formData.goals.length > 1) {
      setFormData(prev => ({
        ...prev,
        goals: prev.goals.filter(goal => goal.id !== goalId)
      }));
    } else {
      toast.warning("At least one goal is required.");
    }
  };

  const handleSave = async () => {
    console.log(`[Progress Summary Form] Saving form data:`, formData);

    // Basic validation
    if (!selectedClient || !formData.clientId) {
      toast.error("Please select a client from the search dropdown.");
      return;
    }

    if (!formData.clientName.trim()) {
      toast.error("Client name is required.");
      return;
    }

    if (!formData.date) {
      toast.error("Date is required.");
      return;
    }

    // Check if at least one goal has a description
    const hasValidGoal = formData.goals.some(goal => goal.description.trim());
    if (!hasValidGoal) {
      toast.error("At least one goal description is required.");
      return;
    }

    try {
      let result;

      if (isEditing) {
        // Update existing progress summary
        result = await dispatch(updateProgressSummary({
          progressSummaryId: editingProgressSummary._id,
          progressSummaryData: formData
        })).unwrap();
        toast.success("Progress Summary updated successfully.");
      } else {
        // Create new progress summary
        result = await dispatch(createProgressSummary(formData)).unwrap();
        toast.success("Progress Summary created successfully.");
      }

      // Call the save function passed from parent (if provided)
      if (onSave) {
        onSave(result);
      }

      onClose();
    } catch (error) {
      console.error(`[Progress Summary Form] Error saving:`, error);
      const errorMessage = error?.message || `Failed to ${isEditing ? 'update' : 'create'} progress summary`;
      toast.error(errorMessage);
    }
  };

  const renderProgressRadio = (goal, field, label) => (
    <td className="px-2 py-2 text-center border-r border-gray-300">
      <input
        type="radio"
        name={`goal-${goal.id}-progress`}
        checked={goal[field]}
        onChange={(e) => handleGoalChange(goal.id, field, e.target.checked)}
        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
      />
    </td>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-sm shadow-xl max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
          <button
            onClick={onClose}
            className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Header Section */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Speech & Language Progress Summary Report
              </h2>
              {formData.clinicAddress ? (
                <>
                  <p className="text-sm text-gray-600">{formData.clinicAddress}</p>
                  <p className="text-sm text-gray-600">
                    Tel: {formData.clinicPhone || 'N/A'} • e-mail: {formData.clinicEmail || 'N/A'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    44 Main St. E., Milton, ON L9T 1N3 • Tel: (905) 875-3345 • Fax: (905) 875-0343
                  </p>
                  <p className="text-sm text-gray-600">e-mail: milton@hearsay.ca</p>
                </>
              )}
            </div>

            {/* Clinic Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Clinic: <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.clinicId}
                onChange={handleClinicSelect}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                <option value="">-- Select a clinic --</option>
                {clinics?.map((clinic) => (
                  <option key={clinic._id} value={clinic._id}>
                    {clinic.name} - {clinic.address.city}
                  </option>
                ))}
              </select>

              {/* Show selected clinic info */}
              {formData.clinicId && selectedClinic && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center text-sm text-blue-800">
                    <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                    <span className="font-medium">
                      {formData.clinicName} - {formData.clinicAddress}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Basic Information Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name: <span className="text-red-500">*</span>
                </label>
                {editingProgressSummary ? (
                  <div className="p-3 bg-gray-50 border border-gray-300 rounded-sm">
                    <div className="flex items-center text-sm text-gray-900">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      <span className="font-medium">{formData.clientName || 'N/A'}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <SearchDropdown
                      key="client-search-dropdown"
                      displayFields={["firstName", "lastName"]}
                      onSelection={handleClientSelect}
                      prefix=":client:"
                      placeholder="Search for client by name..."
                      initialSelection={selectedClient}
                      initialValue={initialSearchValue}
                      separator=" "
                    />

                    {/* Show selected client info */}
                    {formData.clientId && selectedClient && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center text-sm text-green-800">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium">Linked to existing client: {formData.clientName}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date: <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  D.O.B.:
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  C.A. (Chronological Age):
                  <span className="ml-2 text-xs text-gray-500 font-normal italic">
                    (Auto-calculated from DOB)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.chronologicalAge}
                  readOnly
                  className="block w-full rounded-sm border-gray-300 bg-gray-50 shadow-sm sm:text-sm cursor-not-allowed"
                  placeholder="Select date of birth to calculate age"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supervising SLP:
                </label>
                {editingProgressSummary ? (
                  <div className="p-3 bg-gray-50 border border-gray-300 rounded-sm">
                    <div className="flex items-center text-sm text-gray-900">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      <span className="font-medium">{formData.supervisingSLP || 'N/A'}</span>
                    </div>
                    {formData.supervisingSLPQualifications && (
                      <div className="ml-7 mt-1 text-xs text-gray-600">
                        {formData.supervisingSLPQualifications}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <SearchDropdown
                      key="supervising-slp-search-dropdown"
                      displayFields={["firstName", "lastName"]}
                      onSelection={handleSupervisingSLPSelect}
                      prefix=":clinician:"
                      placeholder="Search for supervising SLP..."
                      initialSelection={selectedSupervisingSLP}
                      initialValue={supervisingSLPSearchValue}
                      separator=" "
                    />

                    {/* Show selected supervising SLP info */}
                    {formData.supervisingSLPId && selectedSupervisingSLP && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center text-sm text-blue-800">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium">Linked to clinician: {formData.supervisingSLP}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade:
                </label>
                <input
                  type="text"
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter grade"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School:
                </label>
                <input
                  type="text"
                  value={formData.school}
                  onChange={(e) => handleInputChange('school', e.target.value)}
                  className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter school name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treating Clinician:
                </label>
                {editingProgressSummary ? (
                  <div className="p-3 bg-gray-50 border border-gray-300 rounded-sm">
                    <div className="flex items-center text-sm text-gray-900">
                      <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                      <span className="font-medium">{formData.clinicians || 'N/A'}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <SearchDropdown
                      key="clinician-search-dropdown"
                      displayFields={["firstName", "lastName"]}
                      onSelection={handleClinicianSelect}
                      prefix=":clinician:"
                      placeholder="Search for treating clinician..."
                      initialSelection={selectedClinician}
                      initialValue={clinicianSearchValue}
                      separator=" "
                    />

                    {/* Show selected treating clinician info */}
                    {formData.clinicianId && selectedClinician && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center text-sm text-blue-800">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium">Linked to clinician: {formData.clinicians}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Current Communication Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Communication Status:
            </label>
            <textarea
              value={formData.currentCommunicationStatus}
              onChange={(e) => handleInputChange('currentCommunicationStatus', e.target.value)}
              rows={4}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Describe the client's current communication status..."
            />
          </div>

          {/* Treatment Plan Section */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treatment Plan:
              </label>
              <textarea
                value={formData.treatmentPlan}
                onChange={(e) => handleInputChange('treatmentPlan', e.target.value)}
                rows={3}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session #:
              </label>
              <input
                type="text"
                value={formData.sessionNumber}
                onChange={(e) => handleInputChange('sessionNumber', e.target.value)}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter session number"
              />
            </div>
          </div>

          {/* Goals and Progress Table */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Goals and Progress</h3>
              <button
                onClick={addGoal}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Goal
              </button>
            </div>

            {/* Treatment Plan Selection */}
            {selectedClient && !isEditing && clientTreatmentPlans?.treatmentPlans && (
              <>
                {clientTreatmentPlans.treatmentPlans.docs && clientTreatmentPlans.treatmentPlans.docs.length > 0 ? (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      Treatment Plans Available ({clientTreatmentPlans.treatmentPlans.docs.length})
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Select a treatment plan to automatically load its goals into this progress summary:
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">
                        Select Treatment Plan:
                      </label>
                      <select
                        value={selectedTreatmentPlan?._id || ""}
                        onChange={handleTreatmentPlanSelect}
                        className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
                        <option value="">-- Select a treatment plan --</option>
                        {clientTreatmentPlans.treatmentPlans.docs.map((plan) => (
                          <option key={plan._id} value={plan._id}>
                            {new Date(plan.date).toLocaleDateString()} - {plan.goals?.length || 0} goal(s)
                            {plan.currentCommunicationStatus && ` - ${plan.currentCommunicationStatus.substring(0, 50)}...`}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedTreatmentPlan && (
                      <div className="mt-3 p-3 bg-white border border-blue-200 rounded-sm">
                        <div className="text-xs font-medium text-gray-700 mb-1">Selected Treatment Plan Details:</div>
                        <div className="text-sm text-gray-600">
                          <div><strong>Date:</strong> {new Date(selectedTreatmentPlan.date).toLocaleDateString()}</div>
                          <div><strong>Goals:</strong> {selectedTreatmentPlan.goals?.length || 0}</div>
                          {selectedTreatmentPlan.currentCommunicationStatus && (
                            <div><strong>Status:</strong> {selectedTreatmentPlan.currentCommunicationStatus.substring(0, 100)}...</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <h4 className="text-sm font-medium text-yellow-900 mb-1">
                      No Treatment Plans Found
                    </h4>
                    <p className="text-sm text-yellow-700">
                      This client does not have any treatment plans yet. You can manually add goals below or create a treatment plan first.
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                      Suggested Additional Goals
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                      Progress Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                      Actions
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.goals.map((goal, index) => (
                    <tr key={goal.id} className="border-b border-gray-300">
                      <td className="px-4 py-3 border-r border-gray-300">
                        <textarea
                          value={goal.description}
                          onChange={(e) => handleGoalChange(goal.id, 'description', e.target.value)}
                          rows={2}
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm resize-none"
                          placeholder={`Enter goal ${index + 1} description...`}
                        />
                      </td>
                      <td className="px-4 py-3 border-r border-gray-300">
                        <select
                          value={getGoalProgressValue(goal)}
                          onChange={(e) => handleGoalProgressChange(goal.id, e.target.value)}
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option value="">Select progress status...</option>
                          <option value="notYetTargeted">Not Yet Targeted</option>
                          <option value="emergingMaximum">Emerging with Maximum Support</option>
                          <option value="emergingModerate">Emerging with Moderate Support</option>
                          <option value="emergingMinimal">Emerging with Minimal Support</option>
                          <option value="achieved">Achieved</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 border-r border-gray-300">
                        <textarea
                          value={goal.actions || ''}
                          onChange={(e) => handleGoalChange(goal.id, 'actions', e.target.value)}
                          rows={2}
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm resize-none"
                          placeholder="Enter actions taken for this goal..."
                        />
                      </td>
                      <td className="px-2 py-3 text-center">
                        {formData.goals.length > 1 && (
                          <button
                            onClick={() => removeGoal(goal.id)}
                            className="text-red-600 hover:text-red-900">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments:
            </label>
            <textarea
              value={formData.comments}
              onChange={(e) => handleInputChange('comments', e.target.value)}
              rows={4}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Enter any additional comments..."
            />
          </div>

          {/* Next Steps Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Next Steps:
            </label>
            <textarea
              value={formData.nextSteps}
              onChange={(e) => handleInputChange('nextSteps', e.target.value)}
              rows={4}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Outline the next steps for treatment..."
            />
          </div>

          {/* Footer Signature Section */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="text-center text-sm text-gray-600">
              {formData.supervisingSLP && (
                <>
                  <p className="font-medium">{formData.supervisingSLP}</p>
                  {formData.supervisingSLPQualifications && (
                    <p>{formData.supervisingSLPQualifications}</p>
                  )}
                </>
              )}
              {!formData.supervisingSLP && (
                <p className="text-gray-400 italic">Supervising SLP will appear here when selected</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createLoading || updateLoading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                createLoading || updateLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}>
              {createLoading || updateLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditing ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditing ? 'Update Progress Summary' : 'Save Progress Summary'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressSummaryForm;